'use client';

import MetricLineChart from '../MetricLineChart';
import MetricBarChart from '../MetricBarChart';

// Sample data: Response time percentiles over time
const percentilesData = [
  { name: 'Week 1', P50: 68, P95: 520, P99: 1150 },
  { name: 'Week 2', P50: 62, P95: 480, P99: 1020 },
  { name: 'Week 3', P50: 55, P95: 420, P99: 890 },
  { name: 'Week 4', P50: 48, P95: 380, P99: 780 },
  { name: 'Week 5', P50: 45, P95: 350, P99: 720 },
  { name: 'Week 6', P50: 42, P95: 320, P99: 650 },
];

// Sample data: Response time by endpoint
const endpointData = [
  { name: 'POST /orders', p95: 1850, budget: 500 },
  { name: 'GET /reports', p95: 1420, budget: 1000 },
  { name: 'POST /search', p95: 780, budget: 500 },
  { name: 'GET /users', p95: 220, budget: 500 },
  { name: 'GET /products', p95: 180, budget: 300 },
  { name: 'GET /health', p95: 45, budget: 100 },
];

// Sample data: Latency heatmap (by hour)
const heatmapData = [
  { name: '00:00', p50: 35, p95: 280 },
  { name: '03:00', p50: 32, p95: 250 },
  { name: '06:00', p50: 38, p95: 320 },
  { name: '09:00', p50: 65, p95: 520 },
  { name: '12:00', p50: 82, p95: 680 },
  { name: '15:00', p50: 78, p95: 620 },
  { name: '18:00', p50: 55, p95: 450 },
  { name: '21:00', p50: 42, p95: 350 },
];

export function APIResponseTimePercentiles() {
  return (
    <div className="my-8">
      <h3 className="text-xl font-semibold text-accent mb-4">📊 Response Time Percentiles</h3>
      <MetricLineChart
        data={percentilesData}
        lines={[
          { dataKey: 'P50', name: 'P50 (Median)', color: '#6FCF97' },
          { dataKey: 'P95', name: 'P95', color: '#4A90E2' },
          { dataKey: 'P99', name: 'P99', color: '#E89841' },
        ]}
        xAxisLabel="Week"
        yAxisLabel="Response Time (ms)"
        targetLine={500}
        height={350}
      />
      <p className="text-sm text-foreground-secondary mt-2">
        Sample data showing consistent improvement across all percentiles. P50 improved from 68ms to 42ms,
        while P95 dropped from 520ms to 320ms. The green line shows the P95 target (500ms). Optimization
        efforts are paying off—maintain this trajectory through continued monitoring and tuning.
      </p>
    </div>
  );
}

export function APIResponseTimeByEndpoint() {
  return (
    <div className="my-8">
      <h3 className="text-xl font-semibold text-accent mb-4">🔍 Slowest Endpoints (P95 Latency)</h3>
      <MetricBarChart
        data={endpointData}
        bars={[
          { dataKey: 'p95', name: 'P95 Response Time (ms)', color: '#E89841' },
        ]}
        xAxisLabel="Endpoint"
        yAxisLabel="Response Time (ms)"
        height={350}
      />
      <p className="text-sm text-foreground-secondary mt-2">
        POST /orders (1,850ms) and GET /reports (1,420ms) significantly exceed their latency budgets.
        Priority optimization targets: add database indexes on orders table, implement caching for report
        data, and consider moving report generation to async background jobs. GET /health, /users, and
        /products are well-optimized and meeting targets.
      </p>
    </div>
  );
}

export function APIResponseTimeHeatmap() {
  return (
    <div className="my-8">
      <h3 className="text-xl font-semibold text-accent mb-4">🔥 Response Time by Hour (24h Pattern)</h3>
      <MetricLineChart
        data={heatmapData}
        lines={[
          { dataKey: 'p50', name: 'P50 (ms)', color: '#6FCF97' },
          { dataKey: 'p95', name: 'P95 (ms)', color: '#E89841' },
        ]}
        xAxisLabel="Time of Day"
        yAxisLabel="Response Time (ms)"
        height={300}
      />
      <p className="text-sm text-foreground-secondary mt-2">
        Latency peaks during business hours (9am-3pm) correlating with traffic volume. P95 reaches
        680ms at noon vs. 250ms at 3am. Consider: auto-scaling during peak hours, caching hot data
        before peak times, or load shedding for non-critical requests during high load.
      </p>
    </div>
  );
}
